import { ReactNode, useEffect, useState, useRef } from "react";
import { EditProductContext } from "./EditProductContext";
import React from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import { notification, Spin } from "antd";
import { useTranslation } from "react-i18next";
import { getProduct, updateProduct } from "../../service";
import { colors } from "../../styles/theme";

export interface EditProductContextProps {
  unsavedChanges: boolean;
  setUnsavedChanges: (props: any) => void;
  name: string;
  setName: (props: any) => void;
  descripotion: string;
  setDescription: (props: any) => void;
  imageUrlS3: string;
  setImageUrlS3: (props: any) => void;
  productBaseVariants: any[];
  setProductBaseVariants: (props: any) => void;
  updateProductMutateAsyncIsLoading: boolean;
  save: () => void;
  extensionsConfig: any;
  setExtensionsConfig: (props: any) => void;
  branchVisibility: any; // { [branchId: number]: { isVisible: boolean, isAvailable: boolean, availabilitySchedules: any } }
  setBranchVisibility: (props: any) => void;
  availabilitySchedules: any; // Deprecated - kept for backward compatibility
  setAvailabilitySchedules: (props: any) => void;
  availableBranches: any;
  setAvailableBranches: (props: any) => void;
  setValidateExtensions: (fn: () => { isValid: boolean; invalidCategories: number[] }) => void;
  productCategory: any; // { id: number, name: string }
  setProductCategory: (props: any) => void;
  onlyInternalSell: boolean;
  setOnlyInternalSell: (props: any) => void;
}

export const EditProductContextProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { t } = useTranslation();
  const { productID } = useParams();
  const [unsavedChanges, setUnsavedChanges] = useState(false);

  const [name, setName] = useState("");
  const [descripotion, setDescription] = useState("");
  const [imageUrlS3, setImageUrlS3] = useState("");
  const [productBaseVariants, setProductBaseVariants] = useState([]);
  const [extensionsConfig, setExtensionsConfig] = useState({});
  const extensionsConfigRef = useRef<any>({});
  extensionsConfigRef.current = extensionsConfig;
  const [branchVisibility, setBranchVisibility] = useState<any>({});
  const [availabilitySchedules, setAvailabilitySchedules] = useState({});
  const [availableBranches, setAvailableBranches] = useState([]);
  const [productCategory, setProductCategory] = useState<any>(null);
  const [onlyInternalSell, setOnlyInternalSell] = useState(false);
  const validateExtensionsRef = useRef<() => { isValid: boolean; invalidCategories: number[] }>(() => ({ isValid: true, invalidCategories: [] }));

  const { isLoading } = useQuery<any, Error>(
    ["/product", productID],
    () => getProduct({ productID: productID }),
    {
      onSuccess(data) {
        setName(data.name);
        setDescription(data.description);
        setImageUrlS3(data.imageUrlS3);
        setProductBaseVariants(data.productBaseVariants);
        setProductCategory(data.productCategory);
        setOnlyInternalSell(data.onlyInternalSell ?? false);

        // Initialize branchVisibility from branches data (includes per-branch schedules)
        const initialBranchVisibility: any = {};
        data.branches?.forEach((branch: any) => {
          initialBranchVisibility[branch.id] = {
            isVisible: branch.isVisible ?? true,
            isAvailable: branch.isAvailable ?? true,
            availabilitySchedules: branch.availabilitySchedules ?? data.availabilitySchedules,
          };
        });
        setBranchVisibility(initialBranchVisibility);

        setAvailabilitySchedules(data.availabilitySchedules); // Deprecated - kept for backward compatibility
        setAvailableBranches(data.branches);
      },
      enabled: !!productID,
    }
  );

  const query = useQueryClient();

  const {
    mutateAsync: updateProductMutateAsync,
    isLoading: updateProductMutateAsyncIsLoading,
  } = useMutation(updateProduct, {
    onSuccess: (data) => {
      setUnsavedChanges(false);
      query.invalidateQueries([`/product`]);
      notification.success({
        message: t("editProductContextProvider.editedSuccess"),
        style: {
          background: colors.successBackground,
        },
        placement: "bottomRight",
      });
    },
    onError: () => {
      query.invalidateQueries([`/product`]);
      setUnsavedChanges(false);
      notification.error({
        message: t("editProductContextProvider.error"),
        style: {
          background: colors.errorBackground,
        },
        placement: "bottomRight",
      });
    },
  });

  const save = () => {
    // Walidacja dodatków przed zapisem
    const validationResult = validateExtensionsRef.current();
    if (!validationResult.isValid) {
      notification.error({
        message: t("editProductContextProvider.validationError"),
        description: t("editProductContextProvider.selectPriceVariantForAllGroups"),
        style: {
          background: colors.errorBackground,
        },
        placement: "bottomRight",
      });
      return;
    }

    // Transform branchVisibility back to branches format for API
    const branchesWithVisibility = availableBranches.map((branch: any) => ({
      ...branch,
      isVisible: branchVisibility[branch.id]?.isVisible ?? true,
      isAvailable: branchVisibility[branch.id]?.isAvailable ?? true,
      availabilitySchedules: branchVisibility[branch.id]?.availabilitySchedules ?? availabilitySchedules,
    }));

    updateProductMutateAsync({
      id: productID,
      productBasicData: {
        name,
        description: descripotion,
        availabilitySchedules: availabilitySchedules, // Deprecated - kept for backward compatibility
        branches: branchesWithVisibility,
        onlyInternalSell,
      },
      variants: productBaseVariants,
      extensionsConfig: extensionsConfig,
    });
  };

  useEffect(() => {
    const config: any = { ...extensionsConfigRef.current };

    // Inicjalizacja konfiguracji dla wariantów z danymi z API
    productBaseVariants.forEach((variant: any) => {
      if (!config[variant?.key]) {
        // Jeśli wariant nie ma jeszcze konfiguracji, stwórz pustą
        config[variant?.key] = {};
      }

      // Dla wariantów z API, dodaj ich dodatki
      variant?.productExtensionVariant?.forEach((e: any) => {
        config[variant.key][e.productExtensionId] = {
          name: e.name,
        };
      });
    });

    // Dla nowych wariantów (bez productExtensionVariant), skopiuj konfigurację z pierwszego wariantu
    const existingVariantKeys = Object.keys(config).filter(key => {
      // Sprawdź czy to nie jest nowy wariant (nowe warianty mają puste lub brakujące konfiguracje)
      return config[key] && Object.keys(config[key]).length > 0;
    });

    if (existingVariantKeys.length > 0) {
      // Użyj pierwszego wariantu z konfiguracją jako szablonu
      const templateKey = existingVariantKeys[0];
      const templateConfig = config[templateKey];

      productBaseVariants.forEach((variant: any) => {
        // Sprawdź czy to nowy wariant (nie ma productExtensionVariant z API)
        if (!variant?.productExtensionVariant || variant.productExtensionVariant.length === 0) {
          // Jeśli wariant jest pusty lub ma pustą konfigurację, skopiuj szablon
          if (!config[variant.key] || Object.keys(config[variant.key]).length === 0) {
            config[variant.key] = { ...templateConfig };
          }
        }
      });
    }

    setExtensionsConfig(config);
  }, [productBaseVariants]);

  return (
    <>
      <Spin fullscreen spinning={isLoading} />
      <EditProductContext.Provider
        value={{
          name,
          setName,
          descripotion,
          setDescription,
          imageUrlS3,
          setImageUrlS3,
          unsavedChanges,
          setUnsavedChanges,
          productBaseVariants,
          setProductBaseVariants,
          updateProductMutateAsyncIsLoading,
          save,
          extensionsConfig,
          setExtensionsConfig,
          branchVisibility,
          setBranchVisibility,
          availabilitySchedules,
          setAvailabilitySchedules,
          availableBranches,
          setAvailableBranches,
          setValidateExtensions: (fn: () => { isValid: boolean; invalidCategories: number[] }) => {
            validateExtensionsRef.current = fn;
          },
          productCategory,
          setProductCategory,
          onlyInternalSell,
          setOnlyInternalSell,
        }}
      >
        {children}
      </EditProductContext.Provider>
    </>
  );
};
